<template>
  <side-bar
    :title="$t('mainAside.settings')"
    :show-close="closable"
  >
    <section class="mr-1 mx-2 min-h-0 flex-1 overflow-y-auto">
      <h3 class="sr-only select-none">
        {{ $t('mainAside.settingsNavigation') }}
      </h3>
      <div class="mb-2">
        <wui-input
          v-model="search"
          :placeholder="$t('mainAside.searchSettings')"
          with-clear
          prepend-icon="searchM"
        />
      </div>

      <nav class="flex flex-col gap-y-px">
        <template
          v-for="[settingName, link] in navLinks"
          :key="settingName"
        >
          <router-link
            v-slot="{ href, navigate, isActive }"
            :to="getPath(link.path)"
            custom
          >
            <a
              :href="href"
              :class="[
                'group rounded-8 p-2 relative hover:bg-bg-base-bg3',
                {
                  'bg-bg-base-bg4 hover:bg-bg-base-bg4': isActive,
                }
              ]"
              @click="navigate"
            >
              <span class="flex items-center gap-x-2">
                <wui-icon
                  :name="link.icon"
                  class="w-[24px] group-hover:text-fg-base-fg3"
                  :class="[isActive ? 'text-fg-base-fg3' : 'text-fg-base-fg14']"
                />
                <span
                  class="text-body-r-14-20"
                  :class="[isActive ? 'text-fg-base-fg3!' : 'text-fg-base-fg22']"
                >
                  {{ linksNamesMap[settingName] }}
                </span>
              </span>
              <span
                v-if="role === 'support' && settingName === 'callsSettings' && (isForwardingEnabled || isDndEnabled)"
                class="bg-primary-tints-200 flex items-center p-1 rounded-8 absolute right-1 top-0 bottom-0 m-auto h-8"
                :title="isForwardingEnabled ? $t('common.forwardEnabledDescription') : $t('common.doNotDisturbWarning')"
              >
                <wui-icon
                  name="infoM"
                />
              </span>
            </a>
            <div
              v-if="link.subLinks && link.subLinks.length && (isActive || isSearchActive)"
              class="flex flex-col px-3 py-1"
            >
              <template
                v-for="{ name, path, isFeatureEnabled, isTauriRoute } in getSublinksToRender(link, settingName)"
                :key="name"
              >
                <router-link
                  v-if="isFeatureEnabled.value && showTauriLink(isTauriRoute)"
                  v-slot="{ href: subLinkHref, navigate: subLinkNavigate, isActive: subLinkIsActive }"
                  :to="{ path: getPath(path) }"
                  custom
                >
                  <a
                    :href="subLinkHref"
                    class="group rounded-8 px-[1.8rem] py-1 hover:bg-bg-base-bg3"
                    :class="{ 'bg-bg-base-bg4!': subLinkIsActive }"
                    @click="subLinkNavigate"
                  >
                    <span
                      class="text-body-r-14-20"
                      :class="[subLinkIsActive ? 'text-fg-base-fg3!' : 'text-fg-base-fg22']"
                    >
                      {{ linksNamesMap[name] }}
                    </span>
                  </a>
                </router-link>
              </template>
            </div>
          </router-link>
        </template>
      </nav>
    </section>
  </side-bar>
</template>

<script setup lang="ts">
import { WuiIcon, WuiInput } from '@wui/common-library'
import { storeToRefs } from 'pinia'
import { ref, shallowRef, onMounted, computed, watch } from 'vue'
import { useRouter, RouteLocationRaw } from 'vue-router'

import { useStoreSettingsForwarding } from '@/entities/forwarding'
import { useStoreProfile } from '@/entities/user-profile'

import { SideBar } from '@/shared/components/sidebar'
import { componentsFeaturesMap } from '@/shared/components/user-feature'
import { useUserData, UserFields } from '@/shared/composables/local-storage'
import { useLocalization } from '@/shared/composables/localization'
import { useUserFeatures, useWebSoftphoneEnabled, useFeatureAccessEmulatorStore } from '@/shared/composables/user-features'
import { UserRoles } from '@/shared/types/router'
import { tauriMode } from '@/shared/utils/import-meta'
import { firstLetterToUpperCaseHandler } from '@/shared/utils/string'

import {
  labelIncludesSettingsSearch,
  Link,
  normalizeSettingsSearch,
  SettingsNames,
  SubLink,
  useSettingsLinks,
} from '../lib'

type DeepSearchApi = {
  matchesDeepSearchForParent: (parent: SettingsNames, normalizedQuery: string) => boolean
  matchesDeepSearchForSublink: (
    parent: SettingsNames,
    subLinkName: string,
    normalizedQuery: string,
  ) => boolean
}

const props = defineProps({
  pathPrefix: {
    type: String,
    default: '',
  },
  closable: {
    type: Boolean,
    default: true,
  },
})

const settingsLinks = useSettingsLinks()
const links = settingsLinks.getSettingsLinks()
const { enabledFeatures, hasFeature, isChatMessagingUiEnabled } = useUserFeatures()
const { webSoftphoneEnabled } = useWebSoftphoneEnabled()
const featureAccessEmulator = useFeatureAccessEmulatorStore()
const {
  isActive: isFeatureAccessEmulatorActive,
  webCallBackorder,
  webToFax,
  forward,
  blackWhiteList,
  voiceMailBoxAccess,
  instantMessaging,
  accessGroupManagement,
  doNotDisturb,
} = storeToRefs(featureAccessEmulator)
const { t } = useLocalization()
const { isVideoCodecsEnabled } = storeToRefs(useStoreProfile())
const { isForwardingEnabled, isDndEnabled } = storeToRefs(useStoreSettingsForwarding())
const { getField } = useUserData()
const role = getField(UserFields.USER_ROLE) as UserRoles

const search = ref('')

const deepSearchApi = shallowRef<DeepSearchApi | null>(null)
let deepSearchLoadPromise: Promise<DeepSearchApi> | null = null

const loadDeepSearchApi = (): Promise<DeepSearchApi> => {
  if (deepSearchApi.value) {
    return Promise.resolve(deepSearchApi.value)
  }
  if (!deepSearchLoadPromise) {
    deepSearchLoadPromise = import('../lib/settings-deep-search-catalog').then((module) => {
      const api: DeepSearchApi = {
        matchesDeepSearchForParent: module.matchesDeepSearchForParent,
        matchesDeepSearchForSublink: module.matchesDeepSearchForSublink,
      }
      deepSearchApi.value = api
      return api
    })
  }
  return deepSearchLoadPromise
}

const matchesDeepSearchForParent = (parent: SettingsNames, query: string): boolean =>
  deepSearchApi.value?.matchesDeepSearchForParent(parent, query) ?? false

const matchesDeepSearchForSublink = (
  parent: SettingsNames,
  subLinkName: string,
  query: string,
): boolean => deepSearchApi.value?.matchesDeepSearchForSublink(parent, subLinkName, query) ?? false

const normalizedSearchQuery = computed(() => normalizeSettingsSearch(search.value))

watch(normalizedSearchQuery, (query) => {
  if (query) {
    void loadDeepSearchApi()
  }
})

const isSearchActive = computed(() => normalizedSearchQuery.value.length > 0)

const filteredLinksByFeature = computed(() => {
  const result: Map<SettingsNames, Link> = new Map()
  for (const [name, link] of links) {
    if (link.isNeedWebSoftphoneEnabled && !webSoftphoneEnabled.value) {
      continue
    }

    if (!link.features.length) {
      result.set(name, link)
    } else {
      const featuresNames = link.features
      for (const featuresName of featuresNames) {
        if (enabledFeatures.value.has(featuresName)) {
          result.set(name, link)
          break
        }
      }
    }
  }
  return result
})

const filteredLinks = computed(() => {
  if (!role) return filteredLinksByFeature.value
  return new Map<SettingsNames, Link>(
    [...filteredLinksByFeature.value].filter(([, v]) => v.roles && v.roles.findIndex((r: string) => r === role) !== -1),
  )
})

const linksNamesMap = computed<{[key: string]: string}>(() => ({
  [SettingsNames.account]: t('common.account'),
  [SettingsNames.confidentiality]: t('mainAside.confidentiality'),
  [SettingsNames.general]: t('mainAside.generalSettings'),
  [SettingsNames.soundAndVideo]: isVideoCodecsEnabled.value ? t('mainAside.soundAndVideoSettings') : t('mainAside.soundSettings'),
  [SettingsNames.calls]: t('mainAside.callsSettings'),
  [SettingsNames.terminals]: t('mainAside.terminalsSettings'),
  [SettingsNames.notifications]: t('mainAside.notificationsSettings'),
  [SettingsNames.interface]: t('mainAside.interface'),
  [SettingsNames.about]: t('about.aboutApp'),
  pinPasswordInput: t('mainAside.pinPasswordInput'),
  [SettingsNames.antiCaller]: t('mainAside.CLIR'),
  subscriberAliases: t('mainAside.subscriberAliases'),
  servicesHandle: t('mainAside.servicesHandle'),
  handleServicesPackages: t('mainAside.handleServicesPackages'),
  desktopApplicationSettings: t('mainAside.desktopApplicationSettings'),
  incomingCallHandling: t('callsSettings.incomingCallHandling'),
  blackWhiteList: t('mainAside.blackWhiteList'),
  followMe: t('mainAside.followMe'),
  speedDial: t('mainAside.speedDial'),
  IVR: t('mainAside.IVR'),
  voiceMailForwarding: t('common.voiceMailForwarding'),
  subscriberPickUp: t('callsSettings.subscriberPickUp'),
  callQueues: t('callsSettings.callQueues'),
  terminalsList: t('terminals.terminalsList'),
  devices: t('common.devices'),
  authorizedSessions: t('terminals.sessions'),
  notificationSettings: t('mainAside.notificationSettings'),
  alarmClock: t('common.alarm'),
  audiofilesManagement: t('common.audioFiles'),
  aSideMenuSettings: t('mainAside.personalSettings'),
  languageSettings: t('mainAside.languageSettings'),
  themeSettings: t('mainAside.colorTheme'),
  chatSettings: t('mainAside.chatSettings'),
  handleContactGroups: t('mainAside.handleContactGroups'),
}))

const showTauriLink = (tauriRoute?: boolean) => {
  if (tauriRoute && tauriMode()) {
    return true
  } else if (tauriRoute && !tauriMode()) {
    return false
  } else {
    return true
  }
}

const filterSublinks = (links: SubLink[]): SubLink[] => {
  if (!role) return links
  return [...links]
    .filter((v) => v.roles && v.roles.findIndex((r: string) => r === role) !== -1)
}

const getVisibleSublinksForLink = (link: Link): SubLink[] => {
  if (!link.subLinks?.length) return []
  return filterSublinks(link.subLinks).filter(
    (sub) => sub.isFeatureEnabled.value && showTauriLink(sub.isTauriRoute),
  )
}

const navLinks = computed(() => {
  const query = normalizedSearchQuery.value
  if (!query) return filteredLinks.value

  const namesMap = linksNamesMap.value
  const result = new Map<SettingsNames, Link>()

  for (const [settingName, link] of filteredLinks.value) {
    const parentLabel = namesMap[settingName] ?? ''
    if (labelIncludesSettingsSearch(parentLabel, query)) {
      result.set(settingName, link)
      continue
    }
    if (matchesDeepSearchForParent(settingName, query)) {
      result.set(settingName, link)
      continue
    }
    const visible = getVisibleSublinksForLink(link)
    if (
      visible.some(
        (sub) =>
          labelIncludesSettingsSearch(namesMap[sub.name] ?? '', query)
          || matchesDeepSearchForSublink(settingName, sub.name, query),
      )
    ) {
      result.set(settingName, link)
    }
  }
  return result
})

const getSublinksToRender = (link: Link, settingName: SettingsNames): SubLink[] => {
  const visible = getVisibleSublinksForLink(link)
  const query = normalizedSearchQuery.value
  if (!query) return visible
  const namesMap = linksNamesMap.value
  const parentLabel = namesMap[settingName] ?? ''
  if (labelIncludesSettingsSearch(parentLabel, query)) return visible
  if (matchesDeepSearchForParent(settingName, query)) return visible
  return visible.filter(
    (sub) =>
      labelIncludesSettingsSearch(namesMap[sub.name] ?? '', query)
      || matchesDeepSearchForSublink(settingName, sub.name, query),
  )
}

const subLinkFeatureHandler = (link: SubLink) => {
  const componentName = firstLetterToUpperCaseHandler(link.name)

  // Персональные настройки (тема) доступны без полного UC / InstantMessaging.
  if (link.name === 'aSideMenuSettings') {
    link.isFeatureEnabled.value = true
    return
  }

  // Настройки чата — только полный UC (webSoftphone + IM).
  if (link.name === 'chatSettings') {
    link.isFeatureEnabled.value = isChatMessagingUiEnabled.value
    return
  }

  const features = componentsFeaturesMap.get(componentName)

  if (!features) {
    return
  }

  let enabled = true
  for (const feature of features) {
    if (!hasFeature(feature)) {
      enabled = false
    }
  }
  link.isFeatureEnabled.value = enabled
}

const setIsFeatureEnabled = () => {
  for (const [, link] of filteredLinks.value) {
    link.subLinks?.forEach((subLink) => {
      subLinkFeatureHandler(subLink)
    })
  }
}

setIsFeatureEnabled()

watch([
  () => enabledFeatures.value.size,
  isChatMessagingUiEnabled,
  isFeatureAccessEmulatorActive,
  webCallBackorder,
  webToFax,
  forward,
  blackWhiteList,
  voiceMailBoxAccess,
  instantMessaging,
  accessGroupManagement,
  doNotDisturb,
], () => {
  setIsFeatureEnabled()
})

const router = useRouter()
const globalHash = ref<string>(router.currentRoute.value.hash)

const setGlobalHash = (hash: string): void => {
  globalHash.value = hash
}

router.beforeEach((to) => {
  setGlobalHash(to.hash)
})

const getPath = (path: string) => {
  return props.pathPrefix ? `${props.pathPrefix}${path}` : path
}

onMounted(() => {
  const isAlreadySettings = !!router.currentRoute.value.path.match(/settings/)

  if (!isAlreadySettings) {
    const firstLink = filteredLinks.value.values().next().value
    if (!firstLink?.path) {
      return
    }
    const path = getPath(firstLink.path)
    const object: RouteLocationRaw = { path }
    const hash = path.split('#')[1]

    if (hash) {
      object.hash = `#${hash}`
    }

    router.push(object)
  }
})
</script>
